/**
 * Centralized Media Validation Utilities
 * Shared validation logic for image and video uploads across the application
 */

/** Maximum image file size: 5 MB */
export const MAX_IMAGE_SIZE_5MB = 5 * 1024 * 1024;
/** Maximum image file size: 10 MB */
export const MAX_IMAGE_SIZE_10MB = 10 * 1024 * 1024;
/** Maximum image file size: 25 MB */
export const MAX_IMAGE_SIZE_25MB = 25 * 1024 * 1024;
/** Default maximum file size used by validation functions (25 MB) */
export const DEFAULT_MAX_IMAGE_SIZE = MAX_IMAGE_SIZE_25MB;

/**
 * Ceiling on the RAW (pre-compression) upload before we even attempt to decode it — distinct
 * from the much smaller per-surface limits enforced on the compressed result. Decoding an
 * image fully loads its uncompressed pixel data into memory regardless of its eventual output
 * size (a 12000x12000 opaque PNG decodes to ~550MB), which can exceed a mobile browser's
 * per-tab memory budget well before any format/size logic runs. 50MB comfortably covers a
 * real phone photo (a 12MB HEIC compresses to a couple MB) while still blocking pathological
 * uploads outright.
 */
export const MAX_RAW_IMAGE_FILE_SIZE_BEFORE_COMPRESSION = 50 * 1024 * 1024;

/** Allowlisted image MIME types for file upload validation */
export const VALID_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "image/avif",
  "image/heic",
  "image/heif",
] as const;

export type ValidImageType = (typeof VALID_IMAGE_TYPES)[number];

/** Comma-separated image MIME types for HTML `accept` attributes */
export const VALID_IMAGE_ACCEPT = VALID_IMAGE_TYPES.join(",");

/** Allowlisted video MIME types for file upload validation */
export const VALID_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"] as const;

export type ValidVideoType = (typeof VALID_VIDEO_TYPES)[number];

/** Comma-separated video MIME types for HTML `accept` attributes */
const VALID_VIDEO_ACCEPT = VALID_VIDEO_TYPES.join(",");

/** Combined image + video accept string for media uploaders that support both */
export const VALID_MEDIA_ACCEPT = `${VALID_IMAGE_ACCEPT},${VALID_VIDEO_ACCEPT}`;

/**
 * Type guard to check if a string is a valid image MIME type
 */
function isValidImageType(type: string): type is ValidImageType {
  return VALID_IMAGE_TYPES.includes(type as ValidImageType);
}

/** Maps an allowlisted image MIME type to its canonical lowercase storage extension. */
const IMAGE_MIME_TO_EXTENSION: Record<ValidImageType, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/gif": "gif",
  "image/webp": "webp",
  "image/svg+xml": "svg",
  "image/avif": "avif",
  "image/heic": "heic",
  "image/heif": "heif",
};

/**
 * Returns the safe storage extension for an image file, derived from its MIME type rather than
 * the attacker-controlled filename. Falls back to `"bin"` for an unrecognized type.
 * @param file - The file to derive a storage extension for
 * @returns The canonical lowercase extension for the file's MIME type
 */
export function getSafeImageExtension(file: File): string {
  return isValidImageType(file.type) ? IMAGE_MIME_TO_EXTENSION[file.type] : "bin";
}

/**
 * Type guard to check if a string is a valid video MIME type
 */
function isValidVideoType(type: string): type is ValidVideoType {
  return VALID_VIDEO_TYPES.includes(type as ValidVideoType);
}

/**
 * Validates an image file for type and size
 * @param file - The file to validate
 * @param maxSize - Maximum file size in bytes (default: 5MB)
 * @returns Error message if invalid, null if valid
 */
export function validateImageFile(file: File, maxSize: number = DEFAULT_MAX_IMAGE_SIZE): string | null {
  // Validate file type
  if (!isValidImageType(file.type)) {
    return "Invalid file type. Please upload a JPEG, PNG, GIF, WebP, SVG, AVIF, or HEIC image.";
  }

  // Validate file size
  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    return `File too large. Please upload an image smaller than ${maxSizeMB}MB.`;
  }

  return null;
}

/**
 * Validates a video file for type and size
 * @param file - The file to validate
 * @param maxSize - Maximum file size in bytes (default: 25MB)
 * @returns Error message if invalid, null if valid
 */
function validateVideoFile(file: File, maxSize: number = DEFAULT_MAX_IMAGE_SIZE): string | null {
  if (!isValidVideoType(file.type)) {
    return "Invalid file type. Please upload an MP4, WebM, or MOV video.";
  }

  if (file.size > maxSize) {
    const maxSizeMB = Math.round(maxSize / (1024 * 1024));
    return `File too large. Please upload a video smaller than ${maxSizeMB}MB.`;
  }

  return null;
}

/**
 * Validates a media file (image or video) for type and size
 * @param file - The file to validate
 * @param maxSize - Maximum file size in bytes (default: 25MB)
 * @returns Error message if invalid, null if valid
 */
export function validateMediaFile(file: File, maxSize: number = DEFAULT_MAX_IMAGE_SIZE): string | null {
  if (isValidImageType(file.type)) {
    return validateImageFile(file, maxSize);
  }
  if (isValidVideoType(file.type)) {
    return validateVideoFile(file, maxSize);
  }
  return "Invalid file type. Please upload an image (JPEG, PNG, GIF, WebP, SVG, AVIF, HEIC) or video (MP4, WebM, MOV).";
}

/** A raster-image allowlist: permitted MIME types and their matching lowercase extensions. */
export interface RasterImageAllowlist {
  /** Permitted MIME types, e.g. `["image/png", "image/jpeg"]`. */
  mimeTypes: readonly string[];
  /** Permitted lowercase filename extensions (no leading dot), e.g. `["png", "jpg", "jpeg"]`. */
  extensions: readonly string[];
}

/**
 * Returns whether a user-selected file is an allowed raster image, validating its MIME type AND
 * its filename extension against the caller's allowlist.
 *
 * Unlike {@link validateImageFile} — whose global {@link VALID_IMAGE_TYPES} intentionally
 * permits `image/svg+xml` for rich-media surfaces — this EXCLUDES SVG by construction (callers
 * pass raster-only allowlists). Use this for avatar/logo surfaces, where an SVG could carry
 * executable script (pen-test Finding 3 stored-XSS); do NOT use `validateImageFile` there.
 * Checking both MIME and extension closes the one-sided gaps each has alone (spoofed `type`
 * vs. a renamed file).
 *
 * NOTE: client-side / defense-in-depth only — a direct `supabase.storage.upload()` bypasses it.
 * The authoritative control is the storage bucket `allowed_mime_types`; see
 * `migrations/20260605_finding3_storage_allowed_mime_types.sql`.
 *
 * @param file - The file the user selected.
 * @param allowed - The permitted MIME types and lowercase extensions for this upload surface.
 * @returns `true` only when both the file's MIME type and extension are in the allowlist.
 */
export function isAllowedRasterImage(file: File, allowed: RasterImageAllowlist): boolean {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  return allowed.mimeTypes.includes(file.type) && allowed.extensions.includes(extension);
}

/** The 8-byte PNG file signature. */
const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a] as const;

/**
 * Checks whether a file's first 8 bytes are the PNG signature. Used to confirm a file
 * declared/named as PNG actually contains PNG bytes, so an attacker can't store other
 * content (e.g. an SVG carrying script) under a `.png` name / `image/png` type and have it
 * later served as something executable (pen-test Finding 3).
 *
 * @param file - The file to inspect.
 * @returns `true` when the leading bytes match the PNG signature.
 */
export async function hasPngMagicBytes(file: File): Promise<boolean> {
  const head = await file.slice(0, PNG_SIGNATURE.length).arrayBuffer();
  const bytes = new Uint8Array(head);
  if (bytes.length < PNG_SIGNATURE.length) return false;
  for (let i = 0; i < PNG_SIGNATURE.length; i += 1) {
    if (bytes[i] !== PNG_SIGNATURE[i]) return false;
  }
  return true;
}

/**
 * Format file size for display
 * @param bytes - File size in bytes
 * @returns Formatted string (e.g., "2.5 MB")
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
